import type { Tables } from "../database.types.ts"

// Incentive statuses and the row GET /incentives returns, shared so the client stops restating them.

// incentives.status is check-constrained text, so the generated type is just string.
export const INCENTIVE_STATUSES = ["draft", "live", "ended", "cancelled"] as const

export type IncentiveStatus = (typeof INCENTIVE_STATUSES)[number]

// The statuses POST /incentives/:id/status can move an incentive to. Nothing goes back to draft.
export const STATUS_CHANGE_TARGETS = ["live", "ended", "cancelled"] as const

export type StatusChangeTarget = (typeof STATUS_CHANGE_TARGETS)[number]

// incentives.period and incentives.unit are check-constrained text too.
export const INCENTIVE_PERIODS = ["weekly", "whole_run"] as const

export type IncentivePeriod = (typeof INCENTIVE_PERIODS)[number]

export const INCENTIVE_UNITS = ["per_route", "threshold"] as const

export type IncentiveUnit = (typeof INCENTIVE_UNITS)[number]

// Every route has exactly one type, the first in this order that fits - reconcile.ts works it out.
export const ROUTE_TYPES = [
  "completed",
  "split_original",
  "split_off",
  "rescue",
  "rescued_not_fault",
  "rescued_fault",
  "abandoned_not_fault",
  "abandoned_fault",
] as const

export type RouteType = (typeof ROUTE_TYPES)[number]

export const ROUTE_CYCLES = ["RC1", "RC2"] as const

export type RouteCycle = (typeof ROUTE_CYCLES)[number]

// incentives.audiences is jsonb in this shape - only the incentives function writes it. depots
// holds depot codes, depots.depot_id, which is how Moneta knows a depot.
export type Audience = { areas: string[]; types: string[]; roles: string[]; depots: string[] }

// What GET /incentives/audience-options returns. A depot's value is its code and its label its name.
export type AudienceOptions = { roles: string[]; depots: { value: string; label: string }[] }

// incentives.app_comms is jsonb in this shape, or null when there's none - only the incentives function writes it.
export type AppComms = {
  title: string
  text: string
  terms: string | null
  show_homepage_banner: boolean
}

export const APP_COMMS_MAX_LENGTHS = { title: 50, text: 200, terms: 1000 } as const

// What a client sends to create an incentive, and what the stored row holds beyond its id and stamps.
export type IncentiveFields = Pick<
  Tables<"incentives">,
  | "name"
  | "start_date"
  | "end_date"
  | "pay_description"
  | "one_route_per_day"
  | "amount"
  | "threshold"
> & {
  period: IncentivePeriod
  route_types: RouteType[]
  route_cycles: RouteCycle[]
  unit: IncentiveUnit
  audiences: Audience[]
  // optional so an edit body without it leaves the saved App Comms alone
  app_comms?: AppComms | null
}

export type LatestPeriod = { week_start: string; drivers_paid: number; total_paid: number }

// by is the user's name, or the audit log's actor label when they have none
export type SetLive = { by: string | null; at: string }

// A subset of what the list returns - the full row comes back too, these are the fields the client reads.
// status is the status as of today, which can differ from the stored one; the rest after it is derived.
export type IncentiveListRow = Pick<
  Tables<"incentives">,
  "id" | "name" | "start_date" | "end_date" | "created_at"
> & {
  status: IncentiveStatus
  app_comms: AppComms | null
  depots: string[]
  driver_types: string[]
  latest_period: LatestPeriod | null
  created_by_name: string | null
  // null until it goes live, and for one cancelled as a draft
  set_live: SetLive | null
}

export type IncentiveRule = Pick<
  IncentiveFields,
  | "period"
  | "pay_description"
  | "route_types"
  | "route_cycles"
  | "one_route_per_day"
  | "amount"
  | "unit"
  | "threshold"
>

export type AudienceField = "area" | "type" | "role" | "depot"

// The driver's value on a field some audience didn't allow. Type and role are assumed from the
// driver's latest earlier block when they had none that day.
export type AudienceMiss = { field: AudienceField; value: string; assumed: boolean }

export type IneligibleReason =
  | "not_closed"
  | "no_payment"
  | "route_type"
  | "route_cycle"
  | "not_in_audience"
  | "no_earlier_block"

// The ticket's own status in the ticket system, which Ops move on from Pending.
export type TicketStatus = "Pending" | "Approved" | "Denied"

// What was raised for a driver in the period: the incentive_tickets row and the ticket it points at.
export type DriverTicket = {
  ticket_id: string
  status: "raised" | "failed"
  error: string | null
  count: number
  amount: number
  raised_at: string
  // null for a failed row, whose ticket was never created
  ticket_status: TicketStatus | null
}

// Waiting while a route Ops can still change in Moneta could change whether the driver meets the
// incentive or what they're owed. Only a ready driver is raised.
export type DriverStatus = "ready" | "waiting" | "not_met"

// What GET /incentives/reconcile returns for each incentive and driver.
export type ReconciledDriver = {
  people_id: string
  person_id: string
  person: string
  depot: string | null
  // Moneta's code and cycle for the driver's latest route, which a pay ticket carries
  depot_id: string | null
  route_cycle: RouteCycle | null
  // Both count days rather than routes when the rule allows one route a day. Settled is the routes that
  // count with their pay approved, possible adds every pending route, whether it counts today or not.
  settled: number
  possible: number
  status: DriverStatus
  // what a ticket is raised for
  settled_route_codes: string[]
  // routes that count today, approved or not
  eligible_route_codes: string[]
  no_block_route_codes: string[]
  // eligible routes that didn't add to the count, because another one already counted that day
  same_day_route_codes: string[]
  // route_type is null for a route that isn't closed, which has no type
  ineligible_routes: {
    route_code: string
    reason: IneligibleReason
    route_type: RouteType | null
    route_cycle: RouteCycle | null
    // only for not_in_audience
    audience_misses?: AudienceMiss[]
  }[]
  // InProgress, or Closed with pay Moneta hasn't approved, so Ops can still change it
  pending_route_codes: string[]
  // routes this driver's substitutes drove, which count and are paid as this driver's
  substitute_routes: { route_code: string; person_id: string; person: string }[]
  // up to, for a waiting driver
  amount_owed: number
  // a whole run raised for in an earlier week
  paid: boolean
  ticket: DriverTicket | null
  // names of the other incentives this driver met in the same week
  other_incentives_met: string[]
}

export type ReconciledIncentive = {
  incentive_id: string
  name: string
  end_date: string | null
  period_start: string
  period_end: string
  rule: IncentiveRule
  drivers_met: number
  total_owed: number
  drivers: ReconciledDriver[]
}

// What raising an incentive's tickets did with each driver who met it, by people id.
export type RaiseResult = {
  raised: string[]
  skipped: string[]
  failed: { people_id: string; error: string }[]
  // waiting on Moneta review, raised by a later run once they're ready
  held: string[]
  // not reached inside the time budget
  deferred: string[]
}
